<template>
  <div class="vlm-configuration">
    <b-row class="pt-2 pb-1">
      <b-col cols="12" md="8">
        <h2 class="mb-25">{{ isEditing ? $t('vlm_configuration.edit_configuration') : $t('vlm_configuration.new_configuration') }}</h2>
      </b-col>
      <b-col cols="12" md="4" class="d-flex justify-content-md-end align-items-center mt-1 mt-md-0">
        <b-button variant="outline-secondary" size="sm" class="mr-1" @click="goToList">
          <feather-icon icon="ArrowLeftIcon" class="mr-50" />
          {{ $t('vlm_configuration.back_to_list') }}
        </b-button>
        <b-badge :variant="form.enabled ? 'light-success' : 'light-secondary'" class="px-1 py-75">
          <feather-icon :icon="form.enabled ? 'ActivityIcon' : 'PauseCircleIcon'" size="15" class="mr-50" />
          {{ form.enabled ? $t('vlm_configuration.active') : $t('vlm_configuration.inactive') }}
        </b-badge>
      </b-col>
    </b-row>

    <b-alert v-if="!canUpdate" show variant="warning" class="mb-2">
      <div class="alert-body">{{ $t('vlm_configuration.read_only') }}</div>
    </b-alert>

    <b-row>
      <b-col cols="12" lg="8">
        <b-card>
          <b-form-group :label="$t('vlm_configuration.name')">
            <b-form-input
              v-model="form.name"
              :disabled="isLoading || !canUpdate"
              :placeholder="$t('vlm_configuration.name_placeholder')"
              maxlength="120"
            />
          </b-form-group>

          <b-form-group :label="$t('vlm_configuration.cameras')">
            <CamerasProxySelect
              v-model="form.camera_proxy_ids"
              :selected-options="selectedCameraOptions"
              :multiple="true"
              :reduce="camera => camera.value"
              :disabled="isLoading || !canUpdate"
              :hint="$t('vlm_configuration.cameras_hint')"
            />
          </b-form-group>

          <b-form-group :label="$t('vlm_configuration.prompt')" class="mt-2">
            <b-form-textarea
              v-model="form.prompt"
              :disabled="isLoading || !canUpdate"
              :placeholder="$t('vlm_configuration.prompt_placeholder')"
              rows="5"
              max-rows="8"
              maxlength="4000"
            />
          </b-form-group>

          <div class="mb-2">
            <label class="d-block">{{ $t('vlm_configuration.presets') }}</label>
            <b-button
              v-for="preset in presets"
              :key="preset.key"
              class="mr-50 mb-50"
              size="sm"
              variant="outline-primary"
              :disabled="!canUpdate"
              @click="applyPreset(preset.value)"
            >
              {{ preset.label }}
            </b-button>
          </div>

          <b-form-group :label="$t('vlm_configuration.model')">
            <b-form-select
              v-model="form.model"
              :disabled="isLoading || !canUpdate"
              :options="modelOptions"
            />
            <small class="text-muted d-block mt-50">{{ selectedModelDescription }}</small>
          </b-form-group>

          <hr class="my-2">

          <h5 class="mb-1">{{ $t('vlm_configuration.analysis_policy') }}</h5>
          <b-form-checkbox
            v-model="form.enabled"
            :disabled="isLoading || !canUpdate"
            switch
            class="mb-2"
          >
            {{ $t('vlm_configuration.enabled') }}
          </b-form-checkbox>

          <b-row>
            <b-col cols="12" md="6">
              <b-form-group :label="$t('vlm_configuration.sampling_interval')">
                <b-form-select
                  v-model="form.sampling_interval_seconds"
                  :disabled="isLoading || !canUpdate"
                  :options="samplingOptions"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group :label="$t('vlm_configuration.confidence_threshold')">
                <b-input-group append="%">
                  <b-form-input
                    v-model.number="form.confidence_threshold"
                    :disabled="isLoading || !canUpdate"
                    type="number"
                    min="50"
                    max="95"
                  />
                </b-input-group>
              </b-form-group>
            </b-col>
          </b-row>

          <div class="d-flex justify-content-end mt-1">
            <b-button variant="outline-secondary" class="mr-1" @click="goToList">
              {{ $t('vlm_configuration.cancel') }}
            </b-button>
            <b-button
              variant="primary"
              :disabled="isLoading || isSaving || !canUpdate || !isValid"
              @click="saveConfiguration"
            >
              <b-spinner v-if="isSaving" small class="mr-50" />
              <feather-icon v-else icon="SaveIcon" class="mr-50" />
              {{ $t('vlm_configuration.save') }}
            </b-button>
          </div>
        </b-card>
      </b-col>

      <b-col cols="12" lg="4">
        <b-card :title="$t('vlm_configuration.summary')" class="summary-card">
          <div class="summary-row">
            <span>{{ $t('vlm_configuration.service_status') }}</span>
            <b-badge :variant="form.enabled ? 'light-success' : 'light-secondary'">
              {{ form.enabled ? $t('vlm_configuration.active') : $t('vlm_configuration.inactive') }}
            </b-badge>
          </div>
          <div class="summary-row">
            <span>{{ $t('vlm_configuration.selected_cameras') }}</span>
            <strong>{{ form.camera_proxy_ids.length }}</strong>
          </div>
          <div class="summary-row">
            <span>{{ $t('vlm_configuration.model') }}</span>
            <strong>{{ selectedModelLabel }}</strong>
          </div>
          <div class="summary-row">
            <span>{{ $t('vlm_configuration.sampling_interval') }}</span>
            <strong>{{ form.sampling_interval_seconds }} {{ $t('vlm_configuration.seconds') }}</strong>
          </div>
          <div class="summary-row">
            <span>{{ $t('vlm_configuration.confidence_threshold') }}</span>
            <strong>{{ form.confidence_threshold }}%</strong>
          </div>
          <div class="summary-row border-bottom-0">
            <span>{{ $t('vlm_configuration.updated_at') }}</span>
            <strong>{{ formattedUpdatedAt }}</strong>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { computed, onMounted, ref } from '@vue/composition-api'
import {
  BAlert,
  BBadge,
  BButton,
  BCard,
  BCol,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BInputGroup,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from '@axios'
import ability from '@/libs/acl/ability'
import CamerasProxySelect from '@/components/CamerasProxySelect.vue'

const defaultConfiguration = () => ({
  name: '',
  camera_proxy_ids: [],
  prompt: '',
  model: 'gemini-2.5-flash',
  sampling_interval_seconds: 30,
  confidence_threshold: 80,
  enabled: false,
  updated_at: null,
  updated_by: null,
})

export default {
  name: 'VlmConfiguration',
  components: {
    BAlert,
    BBadge,
    BButton,
    BCard,
    BCol,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BInputGroup,
    BRow,
    BSpinner,
    CamerasProxySelect,
  },
  setup(_, { root }) {
    const router = root.$router
    const t = (key, params) => root.$t(key, params)
    const toast = useToast()
    const form = ref(defaultConfiguration())
    const selectedCameraOptions = ref([])
    const isLoading = ref(false)
    const isSaving = ref(false)
    const canUpdate = computed(() => ability.can('update', 'AiAnalyticServer'))
    const configurationId = computed(() => root.$route.params.id)
    const isEditing = computed(() => Boolean(configurationId.value))

    const modelOptions = computed(() => [
      { value: 'gemini-2.5-flash', text: t('vlm_configuration.model_gemini_flash') },
      { value: 'gemini-2.5-pro', text: t('vlm_configuration.model_gemini_pro') },
      { value: 'gpt-5.6-terra', text: t('vlm_configuration.model_openai') },
    ])
    const samplingOptions = computed(() => [15, 30, 60].map(value => ({
      value,
      text: `${value} ${t('vlm_configuration.seconds')}`,
    })))
    const presets = computed(() => [
      { key: 'fallen', label: t('vlm_configuration.fallen_person'), value: t('vlm_configuration.preset_fallen') },
      { key: 'fight', label: t('vlm_configuration.fight'), value: t('vlm_configuration.preset_fight') },
      { key: 'disorder', label: t('vlm_configuration.disorder'), value: t('vlm_configuration.preset_disorder') },
    ])
    const selectedModel = computed(() => modelOptions.value.find(option => option.value === form.value.model))
    const selectedModelLabel = computed(() => selectedModel.value?.text ?? form.value.model)
    const selectedModelDescription = computed(() => (form.value.model === 'gpt-5.6-terra'
      ? t('vlm_configuration.model_openai_description')
      : t('vlm_configuration.model_gemini_description')))
    const formattedUpdatedAt = computed(() => form.value.updated_at
      ? new Date(form.value.updated_at).toLocaleString()
      : t('vlm_configuration.not_saved'))
    const isValid = computed(() => (
      form.value.camera_proxy_ids.length > 0
      && form.value.name.trim().length > 0
      && form.value.prompt.trim().length > 0
      && [15, 30, 60].includes(Number(form.value.sampling_interval_seconds))
      && Number(form.value.confidence_threshold) >= 50
      && Number(form.value.confidence_threshold) <= 95
    ))

    const notify = (title, variant) => {
      toast({
        component: ToastificationContent,
        props: { title, icon: variant === 'success' ? 'CheckCircleIcon' : 'AlertTriangleIcon', variant },
      })
    }

    const applyConfiguration = configuration => {
      form.value = {
        ...defaultConfiguration(),
        ...configuration,
        camera_proxy_ids: configuration.camera_proxy_ids || [],
      }
      selectedCameraOptions.value = configuration.selected_cameras || []
    }

    const loadConfiguration = async () => {
      isLoading.value = true
      try {
        if (!isEditing.value) return

        const response = await axios.get(`v1/vlm/configurations/${configurationId.value}`)
        applyConfiguration(response.data.data)
      } catch (error) {
        notify(error?.response?.data?.message || t('vlm_configuration.load_error'), 'danger')
      } finally {
        isLoading.value = false
      }
    }

    const applyPreset = prompt => {
      form.value.prompt = prompt
    }

    const saveConfiguration = async () => {
      if (!isValid.value || !canUpdate.value) return

      isSaving.value = true
      try {
        const payload = {
          ...form.value,
          sampling_interval_seconds: Number(form.value.sampling_interval_seconds),
          confidence_threshold: Number(form.value.confidence_threshold),
        }
        const response = isEditing.value
          ? await axios.put(`v1/vlm/configurations/${configurationId.value}`, payload)
          : await axios.post('v1/vlm/configurations', payload)
        applyConfiguration(response.data.data)
        notify(
          isEditing.value ? t('vlm_configuration.updated') : t('vlm_configuration.created'),
          'success'
        )
        router.push({ name: 'vlm_configuration' })
      } catch (error) {
        notify(error?.response?.data?.message || t('vlm_configuration.save_error'), 'danger')
      } finally {
        isSaving.value = false
      }
    }

    const goToList = () => router.push({ name: 'vlm_configuration' })

    onMounted(loadConfiguration)

    return {
      applyPreset,
      canUpdate,
      form,
      formattedUpdatedAt,
      isLoading,
      isSaving,
      isEditing,
      isValid,
      modelOptions,
      presets,
      samplingOptions,
      saveConfiguration,
      goToList,
      selectedCameraOptions,
      selectedModelDescription,
      selectedModelLabel,
    }
  },
}
</script>

<style lang="scss" scoped>
.vlm-configuration {
  padding: 0 15px;
}

.summary-card {
  position: sticky;
  top: 1rem;
}

.summary-row {
  align-items: center;
  border-bottom: 1px solid #ebe9f1;
  display: flex;
  font-size: 0.9rem;
  justify-content: space-between;
  padding: 0.8rem 0;
}

.summary-row strong {
  margin-left: 1rem;
  text-align: right;
}
</style>
